import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    ChartBar, 
    FileText, 
    Download, 
    Calendar,
    Table as TableIcon,
    FileXls,
    FileCsv,
    MagnifyingGlass,
    Funnel,
    ArrowsClockwise,
    Export,
    Database,
    Users,
    Buildings,
    Lightning,
    FileDoc,
    CaretDown,
    CaretUp,
    CheckCircle,
    XCircle,
    Clock
} from '@phosphor-icons/react';
import api from '../lib/api';

// Column categories with icons
const COLUMN_CATEGORIES = {
    'Identificativi': { icon: Database, color: 'text-blue-600' },
    'Cliente': { icon: Users, color: 'text-green-600' },
    'Immobile': { icon: Buildings, color: 'text-amber-600' },
    'Impianto': { icon: Lightning, color: 'text-purple-600' },
    'Contratto': { icon: FileDoc, color: 'text-red-600' },
    'Workflow': { icon: ArrowsClockwise, color: 'text-cyan-600' },
};

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = [
    'codice_contratto',
    'codice_cliente',
    'cliente_nome',
    'cliente_cognome',
    'cliente_ragione_sociale',
    'sito_citta',
    'sito_provincia',
    'impianto_pod',
    'impianto_potenza_nominale_kw',
    'contratto_stato',
    'workflow_progress_percentage',
];

export default function ReportsPage() {
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('tabella');
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');
    
    // Column visibility
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    
    // Sorting
    const [sortColumn, setSortColumn] = useState('codice_contratto');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Load data on mount
    useEffect(() => {
        loadReportData();
        loadStats();
    }, []);
    
    const loadReportData = async () => {
        setLoading(true);
        try {
            const response = await api.post('/reports/export', {});
            setData(response.data.data || []);
            setColumns(response.data.columns || []);
        } catch (error) {
            console.error('Error loading report data:', error);
            toast.error('Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    };
    
    const loadStats = async () => {
        try {
            const response = await api.get('/master-data/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };
    
    const handleExportCSV = async () => {
        setExporting(true);
        try {
            const response = await api.get('/reports/export/csv', {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report_commesse_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Export CSV completato');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Errore durante l\'export');
        } finally {
            setExporting(false);
        }
    };
    
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const response = await api.get('/reports/export/excel', {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report_commesse_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success('Export Excel completato');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Errore durante l\'export');
        } finally {
            setExporting(false);
        }
    };
    
    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];
        
        // Search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            result = result.filter(row => 
                Object.values(row).some(val => 
                    val && String(val).toLowerCase().includes(search)
                )
            );
        }
        
        // Status filter
        if (statusFilter !== 'all') {
            result = result.filter(row => row.contratto_stato === statusFilter);
        }
        
        // Region filter
        if (regionFilter !== 'all') {
            result = result.filter(row => row.sito_regione === regionFilter);
        }
        
        // Sorting
        if (sortColumn) {
            result.sort((a, b) => {
                const aVal = a[sortColumn] || '';
                const bVal = b[sortColumn] || '';
                
                if (sortDirection === 'asc') {
                    return String(aVal).localeCompare(String(bVal));
                } else {
                    return String(bVal).localeCompare(String(aVal));
                }
            });
        }
        
        return result;
    }, [data, searchTerm, statusFilter, regionFilter, sortColumn, sortDirection]);
    
    // Get unique values for filters
    const uniqueStatuses = useMemo(() => {
        const statuses = new Set(data.map(d => d.contratto_stato).filter(Boolean));
        return Array.from(statuses);
    }, [data]);
    
    const uniqueRegions = useMemo(() => {
        const regions = new Set(data.map(d => d.sito_regione).filter(Boolean));
        return Array.from(regions);
    }, [data]);
    
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };
    
    const toggleColumn = (columnKey) => {
        setVisibleColumns(prev => 
            prev.includes(columnKey)
                ? prev.filter(c => c !== columnKey)
                : [...prev, columnKey]
        );
    };
    
    // Group columns by category
    const columnsByCategory = useMemo(() => {
        const groups = {};
        columns.forEach(col => {
            const category = col.category || 'Altro';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(col);
        });
        return groups;
    }, [columns]);
    
    const getColumnLabel = (key) => {
        const col = columns.find(c => c.key === key);
        return col ? col.label : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };
    
    const formatCellValue = (value, key) => {
        if (value === null || value === undefined || value === '') {
            return <span className="text-gray-400">-</span>;
        }
        
        // Progress percentage
        if (key.includes('progress') || key.includes('percentage')) {
            const num = parseFloat(value);
            return (
                <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-primary rounded-full" 
                            style={{ width: `${Math.min(num, 100)}%` }}
                        />
                    </div>
                    <span className="text-xs font-medium">{num.toFixed(0)}%</span>
                </div>
            );
        }
        
        // Status fields
        if (key.includes('stato')) {
            const statusColors = {
                'in_corso': 'bg-blue-100 text-blue-800',
                'completato': 'bg-green-100 text-green-800',
                'sospeso': 'bg-amber-100 text-amber-800',
                'annullato': 'bg-red-100 text-red-800',
            };
            const color = statusColors[value] || 'bg-gray-100 text-gray-800';
            return <Badge className={color}>{value}</Badge>;
        }
        
        // Boolean fields
        if (typeof value === 'boolean' || value === 'true' || value === 'false') {
            const boolVal = value === true || value === 'true';
            return boolVal ? (
                <CheckCircle weight="fill" className="h-5 w-5 text-green-500" />
            ) : (
                <XCircle weight="fill" className="h-5 w-5 text-gray-300" />
            );
        }
        
        // Date fields
        if (key.includes('data') || key.includes('_at')) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('it-IT');
                }
            } catch (error) {
                console.error('Date parsing error:', error);
            }
        }
        
        return String(value);
    };
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="reports-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-xl font-semibold">Reportistica</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Dati consolidati delle commesse - Base dati master CRM
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={loadReportData}
                        disabled={loading}
                    >
                        <ArrowsClockwise className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Aggiorna
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleExportCSV}
                        disabled={exporting || loading}
                    >
                        <FileCsv className="h-4 w-4 mr-2" />
                        CSV
                    </Button>
                    <Button 
                        size="sm" 
                        onClick={handleExportExcel}
                        disabled={exporting || loading}
                    >
                        <FileXls className="h-4 w-4 mr-2" />
                        Excel
                    </Button>
                </div>
            </div>
            
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <FileDoc className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.master_contracts}</p>
                                    <p className="text-xs text-muted-foreground">Contratti</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Users className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.master_customers}</p>
                                    <p className="text-xs text-muted-foreground">Clienti</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <Buildings className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.master_sites}</p>
                                    <p className="text-xs text-muted-foreground">Siti</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Lightning className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.master_plants}</p>
                                    <p className="text-xs text-muted-foreground">Impianti</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-cyan-100 rounded-lg">
                                    <ArrowsClockwise className="h-5 w-5 text-cyan-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.workflow_runtime}</p>
                                    <p className="text-xs text-muted-foreground">Workflow</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <Database className="h-5 w-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.contract_nodes}</p>
                                    <p className="text-xs text-muted-foreground">Step</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
            
            {/* Main Content */}
            <Card>
                <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <TableIcon weight="fill" className="h-5 w-5 text-primary" />
                                Export Dati Consolidati
                            </CardTitle>
                            <CardDescription>
                                {filteredData.length} di {data.length} record • {visibleColumns.length} colonne visibili
                            </CardDescription>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowColumnSelector(!showColumnSelector)}
                        >
                            <Funnel className="h-4 w-4 mr-2" />
                            Colonne
                            {showColumnSelector ? <CaretUp className="h-4 w-4 ml-2" /> : <CaretDown className="h-4 w-4 ml-2" />}
                        </Button>
                    </div>
                </CardHeader>
                
                {/* Column Selector */}
                {showColumnSelector && (
                    <div className="px-6 pb-4 border-b">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {Object.entries(columnsByCategory).map(([category, cols]) => {
                                const CategoryIcon = COLUMN_CATEGORIES[category]?.icon || Database;
                                const iconColor = COLUMN_CATEGORIES[category]?.color || 'text-gray-600';
                                
                                return (
                                    <div key={category} className="space-y-2">
                                        <Label className={`text-xs font-medium flex items-center gap-1 ${iconColor}`}>
                                            <CategoryIcon className="h-3 w-3" />
                                            {category}
                                        </Label>
                                        <div className="space-y-1">
                                            {cols.map(col => (
                                                <label key={col.key} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns.includes(col.key)}
                                                        onChange={() => toggleColumn(col.key)}
                                                        className="rounded border-gray-300"
                                                    />
                                                    {col.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                
                {/* Filters */}
                <div className="px-6 pb-4 flex items-center gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                            placeholder="Cerca in tutti i campi..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Stato" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti gli stati</SelectItem>
                            {uniqueStatuses.map(status => (
                                <SelectItem key={status} value={status}>{status}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Regione" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutte le regioni</SelectItem>
                            {uniqueRegions.map(region => (
                                <SelectItem key={region} value={region}>{region}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-4">
                            {[1,2,3,4,5].map(i => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="p-12 text-center">
                            <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">Nessun dato trovato</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Prova a modificare i filtri o esegui la migrazione dei dati
                            </p>
                        </div>
                    ) : (
                        <ScrollArea className="w-full">
                            <div className="min-w-max">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            {visibleColumns.map(colKey => (
                                                <TableHead 
                                                    key={colKey}
                                                    className="cursor-pointer hover:bg-muted whitespace-nowrap"
                                                    onClick={() => handleSort(colKey)}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        {getColumnLabel(colKey)}
                                                        {sortColumn === colKey && (
                                                            sortDirection === 'asc' 
                                                                ? <CaretUp className="h-4 w-4" />
                                                                : <CaretDown className="h-4 w-4" />
                                                        )}
                                                    </div>
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredData.map((row, idx) => (
                                            <TableRow key={row.contract_id || idx} className="hover:bg-muted/30">
                                                {visibleColumns.map(colKey => (
                                                    <TableCell key={colKey} className="whitespace-nowrap">
                                                        {formatCellValue(row[colKey], colKey)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
